import React from 'react'
import { X } from 'lucide-react'
import ElevationProfile from './ElevationProfile'
import { ProfilePoint } from '../utils/elevationProfile'

interface DayDetailsPanelProps {
  isOpen: boolean
  onClose: () => void
  dayData?: {
    dayNumber: number
    color?: string
    date: string
    distance_km: number
    elevation_gain_m: number
    elevation_loss_m?: number
    profile?: ProfilePoint[]
    duration_hours: number
    start_point: {
      name: string
      coordinates: [number, number]
    }
    end_point: {
      name: string
      coordinates: [number, number]
    }
    stats?: {
      max_elevation_m: number
      min_elevation_m: number
      avg_speed_kmh: number
      max_speed_kmh: number
    }
  } | null
}

const DayDetailsPanel = ({ isOpen, onClose, dayData }: DayDetailsPanelProps) => {
  // Don't render anything if we've never been opened
  // But once opened, keep rendering for animation even when closing
  const [hasBeenOpened, setHasBeenOpened] = React.useState(false)
  
  React.useEffect(() => {
    if (isOpen) setHasBeenOpened(true)
  }, [isOpen])
  
  if (!hasBeenOpened) return null

  return (
    <>
      {/* Panel with strong shadow instead of backdrop */}
      <div 
        className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-xl z-50 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-y-0 shadow-[0_-10px_40px_rgba(0,0,0,0.3)]' : 'translate-y-full'
        }`}
        style={{ maxHeight: '70vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            {dayData ? `Day ${dayData.dayNumber}` : 'Day Details'}
          </h2>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(70vh - 80px)' }}>
          {!dayData ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading day data...</p>
            </div>
          ) : (
            <>
              {/* Date */}
              <div className="mb-4">
                <p className="text-lg text-gray-600">{new Date(dayData.date).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</p>
              </div>

          {/* Key Stats Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-blue-700">{dayData.distance_km}km</div>
              <div className="text-sm text-blue-600">Distance</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3">
              <div className="text-xl font-bold text-green-700">↑ {dayData.elevation_gain_m}m</div>
              <div className="text-xl font-bold text-amber-700">↓ {dayData.elevation_loss_m ?? 0}m</div>
              <div className="text-sm text-green-600">Ascent / Descent</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-purple-700">{Math.round(dayData.duration_hours * 10) / 10}h</div>
              <div className="text-sm text-purple-600">Duration</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-orange-700">{Math.round((dayData.stats?.avg_speed_kmh || (dayData.distance_km / dayData.duration_hours) || 0) * 10) / 10}</div>
              <div className="text-sm text-orange-600">Avg Speed (km/h)</div>
            </div>
          </div>

          {/* Route Details */}
          <div className="space-y-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Route Details</h3>
            
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div
                  className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0"
                  style={{ backgroundColor: dayData.color || '#6b7280' }}
                ></div>
                <div>
                  <div className="font-medium text-gray-900">Start Point</div>
                  <div className="text-sm text-gray-600">{dayData.start_point.name}</div>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div
                  className="w-3 h-3 rounded-full border-2 bg-white mt-1.5 flex-shrink-0"
                  style={{ borderColor: dayData.color || '#6b7280' }}
                ></div>
                <div>
                  <div className="font-medium text-gray-900">End Point</div>
                  <div className="text-sm text-gray-600">{dayData.end_point.name}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Elevation Profile */}
          {((dayData.profile && dayData.profile.length > 1) ||
            (dayData.stats && (dayData.stats.max_elevation_m > 0 || dayData.stats.min_elevation_m > 0))) && (
            <div className="space-y-3 mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Elevation Profile</h3>

              {dayData.profile && dayData.profile.length > 1 && (
                <ElevationProfile data={dayData.profile} color={dayData.color || '#3b82f6'} />
              )}

              {dayData.stats && (dayData.stats.max_elevation_m > 0 || dayData.stats.min_elevation_m > 0) && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-lg font-bold text-gray-900">{dayData.stats.max_elevation_m}m</div>
                    <div className="text-sm text-gray-600">Highest Point</div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-lg font-bold text-gray-900">{dayData.stats.min_elevation_m}m</div>
                    <div className="text-sm text-gray-600">Lowest Point</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Speed Stats */}
          <div className="space-y-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Speed Analysis</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-lg font-bold text-gray-900">{Math.round((dayData.stats?.avg_speed_kmh || (dayData.distance_km / dayData.duration_hours) || 0) * 10) / 10} km/h</div>
                <div className="text-sm text-gray-600">Average Speed</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-lg font-bold text-gray-900">{Math.round((dayData.stats?.max_speed_kmh || 0) * 10) / 10} km/h</div>
                <div className="text-sm text-gray-600">Max Speed</div>
              </div>
            </div>
          </div>

            </>
          )}
        </div>
      </div>
    </>
  )
}

export default DayDetailsPanel